import {
  type JSX,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { supabase, useSession } from "@/lib/supabase";
import {
  DEFAULT_THEME,
  DOCS_THEME_STORAGE_KEY,
  THEME_STORAGE_KEY,
  type Theme,
  applyTheme,
  isPublicDocsOrigin,
  isTheme,
  normalizePersistedTheme,
  readPersistedTheme,
} from "@/lib/theme";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Single source of truth for the active theme. Applies the theme to the DOM and
 * mirrors it to `localStorage` on every change, and reconciles with the
 * authenticated user's `theme` metadata so the choice follows them across
 * devices. The pre-React bootstrap in `main.tsx` already painted the first
 * frame; this provider keeps React state in sync from there on.
 */
export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();
  const [theme, setThemeState] = useState<Theme>(readPersistedTheme);

  useEffect(() => {
    applyTheme(theme);
    // On the public docs origin only a selection is stored (see `setTheme`):
    // storing what was merely shown would hide the system's theme from then on.
    if (!isPublicDocsOrigin()) localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // When the session arrives, prefer the server-side preference as truth. If
  // a retired value is present, migrate it to navy. Missing/invalid metadata
  // is seeded with the local preference.
  const userId = session.status === "authenticated" ? session.session.user.id : undefined;
  const metaTheme =
    session.status === "authenticated" ? session.session.user.user_metadata?.theme : undefined;
  useEffect(() => {
    if (!userId) return;
    const normalizedTheme = normalizePersistedTheme(metaTheme);
    if (normalizedTheme) {
      setThemeState((prev) => (prev === normalizedTheme ? prev : normalizedTheme));
    }
    if (!isTheme(metaTheme)) {
      void supabase.auth
        .updateUser({ data: { theme: normalizedTheme ?? readPersistedTheme() } })
        .catch(() => {
          // Keep the supported local theme; a later session reconciliation
          // can retry without turning a network failure into a render loop.
        });
    }
  }, [userId, metaTheme]);

  const setTheme = useCallback(
    (next: Theme): void => {
      if (isPublicDocsOrigin()) localStorage.setItem(DOCS_THEME_STORAGE_KEY, next);
      setThemeState((prev) => {
        if (prev === next) return prev;
        if (session.status === "authenticated") {
          void supabase.auth.updateUser({ data: { theme: next } });
        }
        return next;
      });
    },
    [session.status],
  );

  const contextValue = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) {
    // Fallback for consumers rendered without the provider. The app always
    // mounts under ThemeProvider, but several existing AppShell tests render
    // <AppShell /> bare; returning the default (rather than throwing) keeps
    // them green without coupling every such test to this provider.
    return { theme: DEFAULT_THEME, setTheme: () => {} };
  }
  return value;
}
